/**
 * useAreaSelection
 *
 * Map-selected monitoring area shared by the Monitoring / Alerts / Imagery
 * tabs. No selection means "all areas" — consumers scope their data only when
 * a value is present, so there is never a "select an area first" state.
 *
 * State lives in the `monitoring` Vuex module (cross-route, project-keyed);
 * these helpers keep the original composable API for consumers.
 *
 * Paint is handed out as expressions merged into FeatureCollectionRenderer's
 * layer options (same constraint as useMetricPaint: setPaintProperty would be
 * reverted by MapLayer's style.load re-add).
 */
import { computed, unref } from 'vue'
import store from '@/vuex/index.js'
import { DEFAULT_COLOR, DEFAULT_FILL_OPACITY } from '@/composables/useMetricPaint'

const DIM_LINE_COLOR = '#9ca3af'

export function setSelectedArea({ projectId, code, name }) {
  store.commit('monitoring/SET_SELECTED_AREA', { projectId, code, name })
}

export function clearSelectedArea() {
  store.commit('monitoring/CLEAR_SELECTED_AREA')
}

/**
 * @param {import('vue').Ref<string>|string} projectId
 * @returns {import('vue').ComputedRef<{code: string, name: string}|null>}
 */
export function useSelectedArea(projectId) {
  return computed(() => {
    const s = store.getters['monitoring/selectedArea']
    if (!s || s.projectId !== String(unref(projectId))) return null
    return { code: s.code, name: s.name }
  })
}

/**
 * @param {import('vue').Ref<string>|string} groupId
 * @returns {import('vue').ComputedRef<{lineWidth: any, lineColor: any, fillOpacity: any}|null>}
 */
export function useSelectionPaint(groupId) {
  return computed(() => {
    const s = store.getters['monitoring/selectedArea']
    if (!s || `project-${s.projectId}` !== unref(groupId)) return null
    const isSelected = ['==', ['get', 'code'], s.code]
    return {
      lineWidth: ['case', isSelected, 4, 2],
      lineColor: ['case', isSelected, DEFAULT_COLOR, DIM_LINE_COLOR],
      fillOpacity: ['case', isSelected, 0.15, DEFAULT_FILL_OPACITY],
    }
  })
}
